import { has } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentClassification } from "@radarly/models/search";
import { isClassificationCardDataviz } from "../dataviz";
import { Card, ClassificationCard } from "../model";
import { ComparisonPeriod } from "../period";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface ClassificationCardFactoryOptions {
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
}

export class ClassificationCardFragmentsFactory extends CardFragmentsFactory {
  static create(input: ClassificationCardFactoryOptions) {
    const factory = new ClassificationCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input: ClassificationCardFactoryOptions) {
    const factory = new ClassificationCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      metric: factory.getMetricFromCardAndInput(card, input),
      comparisonPeriod: factory.getComparaisonPeriod(
        card,
        "previous_period",
        input
      ),
    });
  }
  private getFragments(input?: ClassificationCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input && has(input, "comparisonPeriod")
        ? input.comparisonPeriod
        : "previous_period"
    );
  }
  private getFragment(
    input?: ClassificationCardFactoryOptions
  ): SearchFragmentClassification {
    const metric = input?.metric || Metric.Doc;
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          metrics: {
            metrics: [metric],
          },
          contentClassification: {
            metrics: [metric],
            sort: {
              on: metric,
              order: "desc",
            },
            size: 400,
          },
        },
      },
    };
  }
}
export class ClassificationCardFactory
  implements ICardFactory<ClassificationCard, ClassificationCardFactoryOptions>
{
  public static getInstance() {
    return new ClassificationCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: ClassificationCardFactoryOptions & {
      dataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<ClassificationCard>().createCard(
      workspaceId,
      projectFocuses,
      "classification",
      card.title,
      ClassificationCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: ClassificationCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<ClassificationCard>().createCard(
      workspaceId,
      projectFocuses,
      "classification",
      title,
      ClassificationCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.HorizontalBarChart)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isClassificationCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.HorizontalBarChart
      ) as ClassificationCard["dataviz"];
    }
  }
}
